<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-calendar-alt me-2"></i>
            Schedule Audits
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="saveSchedule">
            <!-- Enable/Disable -->
            <div class="mb-3">
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="enableSchedule"
                  v-model="form.enabled"
                >
                <label class="form-check-label" for="enableSchedule">
                  Enable scheduled audits
                </label>
              </div>
            </div>

            <template v-if="form.enabled">
              <!-- Frequency -->
              <div class="mb-3">
                <label for="frequency" class="form-label">Frequency</label>
                <select
                  class="form-select"
                  id="frequency"
                  v-model="form.frequency"
                  required
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <!-- Day of Week (for weekly) -->
              <div class="mb-3" v-if="form.frequency === 'weekly'">
                <label for="dayOfWeek" class="form-label">Day of Week</label>
                <select
                  class="form-select"
                  id="dayOfWeek"
                  v-model="form.day_of_week"
                  required
                >
                  <option :value="0">Sunday</option>
                  <option :value="1">Monday</option>
                  <option :value="2">Tuesday</option>
                  <option :value="3">Wednesday</option>
                  <option :value="4">Thursday</option>
                  <option :value="5">Friday</option>
                  <option :value="6">Saturday</option>
                </select>
              </div>

              <!-- Day of Month (for monthly) -->
              <div class="mb-3" v-if="form.frequency === 'monthly'">
                <label for="dayOfMonth" class="form-label">Day of Month</label>
                <select
                  class="form-select"
                  id="dayOfMonth"
                  v-model="form.day_of_month"
                  required
                >
                  <option v-for="day in 31" :key="day" :value="day">
                    {{ day }}{{ getOrdinalSuffix(day) }}
                  </option>
                </select>
              </div>

              <!-- Time -->
              <div class="mb-3">
                <label for="time" class="form-label">Time</label>
                <input
                  type="time"
                  class="form-control"
                  id="time"
                  v-model="form.time"
                  required
                >
                <small class="form-text text-muted">
                  Time when audit should run (server timezone)
                </small>
              </div>

              <!-- Next Run Info -->
              <div class="alert alert-info" v-if="nextRunPreview">
                <i class="fas fa-info-circle me-2"></i>
                <strong>Next Run:</strong> {{ nextRunPreview }}
              </div>
            </template>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="saveSchedule"
            :disabled="saving"
          >
            <span v-if="saving">
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
              Saving...
            </span>
            <span v-else>
              <i class="fas fa-save me-2"></i>
              Save Schedule
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'ScheduleModal',
  props: {
    clientId: {
      type: [String, Number],
      required: true
    },
    currentSchedule: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      saving: false,
      form: {
        enabled: false,
        frequency: 'weekly',
        day_of_week: 1, // Monday
        day_of_month: 1,
        time: '02:00'
      }
    }
  },

  computed: {
    nextRunPreview() {
      if (!this.form.enabled) return null

      const now = new Date()
      let nextRun = new Date()

      // Parse time
      const [hour, minute] = this.form.time.split(':')
      nextRun.setHours(parseInt(hour), parseInt(minute), 0, 0)

      switch (this.form.frequency) {
        case 'daily': {
          if (nextRun <= now) {
            nextRun.setDate(nextRun.getDate() + 1)
          }
          break
        }

        case 'weekly': {
          const targetDay = parseInt(this.form.day_of_week)
          const currentDay = now.getDay()
          let daysUntilTarget = targetDay - currentDay
          if (daysUntilTarget <= 0) {
            daysUntilTarget += 7
          }
          nextRun.setDate(now.getDate() + daysUntilTarget)
          break
        }

        case 'monthly': {
          nextRun.setDate(parseInt(this.form.day_of_month))
          if (nextRun <= now) {
            nextRun.setMonth(nextRun.getMonth() + 1)
          }
          break
        }
      }

      return nextRun.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    }
  },

  mounted() {
    // Load current schedule if exists
    if (this.currentSchedule) {
      this.form = {
        enabled: this.currentSchedule.enabled,
        frequency: this.currentSchedule.frequency,
        day_of_week: this.currentSchedule.day_of_week || 1,
        day_of_month: this.currentSchedule.day_of_month || 1,
        time: this.currentSchedule.time || '02:00'
      }
    }
  },

  methods: {
    async saveSchedule() {
      this.saving = true
      try {
        const response = await api.post(
          `/juniper/ada-auditor/clients/${this.clientId}/schedule`,
          this.form
        )

        this.$emit('saved', response.data.schedule)
      } catch (error) {
        console.error('Failed to save schedule:', error)
        this.$toast?.error('Failed to save schedule')
      } finally {
        this.saving = false
      }
    },

    getOrdinalSuffix(day) {
      if (day > 3 && day < 21) return 'th'
      switch (day % 10) {
        case 1: return 'st'
        case 2: return 'nd'
        case 3: return 'rd'
        default: return 'th'
      }
    }
  }
}
</script>

<style scoped>
.modal {
  display: block;
}
</style>
